import React, { useState, useEffect, Fragment } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { HiChevronLeft } from "react-icons/hi";
import { HiTrash, HiArrowPath } from "react-icons/hi2";
import { Dialog, Transition } from "@headlessui/react";
import {
  ActivityMeasurement,
  SessionTemplate,
  Session,
  PeriodisationType,
  PERIODISATION_OPTIONS,
} from "../../types/post";
import { TrainingType } from "../../types/activity";
import { sessionService } from "../../services/sessionService";
import { trainingTypeService } from "../../services/trainingTypeService";
import MasterSessionDetails from "./MasterSessionDetails";
import SearchableSelect from "../common/SearchableSelect";

interface ManageSessionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSessionCreated?: () => void;
  initialTemplate?: (SessionTemplate & { id: string }) | null;
  existingSession?: ((Session | SessionTemplate) & { id: string }) | null;
  onDelete?: (session: (Session | SessionTemplate) & { id: string }) => void;
  onCreateNew?: () => void;
}

const ManageSessionModal: React.FC<ManageSessionModalProps> = ({
  isOpen,
  onClose,
  onSessionCreated,
  initialTemplate,
  existingSession,
  onDelete,
  onCreateNew,
}) => {
  const { currentUser, userData } = useAuth();

  // Form state
  const [sessionName, setSessionName] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [plannedMeasurements, setPlannedMeasurements] = useState<
    ActivityMeasurement[]
  >([]);
  const [trainingTypeID, setTrainingTypeID] = useState<string>("");
  const [trainingTypes, setTrainingTypes] = useState<TrainingType[]>([]);
  const [periodisation, setPeriodisation] = useState<PeriodisationType | "">(
    "",
  );

  // Loading and error states
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Confirmation dialog state
  const [showResetConfirmation, setShowResetConfirmation] =
    useState<boolean>(false);

  // Reset key to force remount of MasterSessionDetails
  const [resetKey, setResetKey] = useState<number>(0);

  // Initialize from existing session (re-runs when trainingTypes load so
  // legacy composite sessionTypeIDs can be resolved)
  useEffect(() => {
    const resolveID = (entity: {
      trainingTypeID?: string;
      sessionTypeID?: string;
    }): string =>
      trainingTypeService.resolveTrainingTypeForSession(entity, trainingTypes)
        ?.id || "";

    if (existingSession) {
      setSessionName(existingSession.name || "");
      setDescription(existingSession.description || "");
      setPlannedMeasurements(existingSession.ActivityMeasurementList || []);
      setTrainingTypeID(resolveID(existingSession));
      setPeriodisation(existingSession.periodisation || "");
    } else if (initialTemplate) {
      setSessionName(initialTemplate.name || "");
      setDescription(initialTemplate.description || "");
      setPlannedMeasurements(initialTemplate.ActivityMeasurementList || []);
      setTrainingTypeID(resolveID(initialTemplate));
      setPeriodisation(initialTemplate.periodisation || "");
    }
  }, [existingSession, initialTemplate, trainingTypes]);

  // Fetch training types when modal opens
  useEffect(() => {
    const fetchTrainingTypes = async () => {
      if (!isOpen) return;

      try {
        const types = await trainingTypeService.fetchTrainingTypesForSports(
          userData?.trainingSportIDs,
          userData?.customTrainingTypes,
        );
        setTrainingTypes(types);
      } catch (error) {
        console.error("Error fetching training types:", error);
      }
    };

    fetchTrainingTypes();
  }, [isOpen, userData?.trainingSportIDs, userData?.customTrainingTypes]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSessionName("");
      setDescription("");
      setPlannedMeasurements([]);
      setTrainingTypeID("");
      setPeriodisation("");
      setError(null);
    }
  }, [isOpen]);

  const handleSave = async () => {
    if (!currentUser?.uid) {
      setError("You must be logged in to manage sessions");
      return;
    }

    if (plannedMeasurements.length === 0) {
      setError("Please add at least one activity to the session");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const sessionData: any = {
        ActivityMeasurementList: plannedMeasurements,
        name: sessionName.trim() || "",
        description: description.trim() || "",
      };

      // Add optional fields
      if (trainingTypeID && trainingTypeID.trim()) {
        sessionData.trainingTypeID = trainingTypeID.trim();
      }

      if (periodisation) {
        sessionData.periodisation = periodisation;
      }

      if (existingSession?.id) {
        // Update existing session template
        await sessionService.updateSessionTemplate(
          currentUser.uid,
          existingSession.id,
          sessionData,
        );
      } else {
        // Create new session template
        await sessionService.createSessionTemplate(
          currentUser.uid,
          sessionData,
        );
      }

      if (onSessionCreated) {
        onSessionCreated();
      }
      onClose();
    } catch (error) {
      console.error("Error saving session:", error);
      setError("Failed to save session. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setSessionName("");
    setDescription("");
    setPlannedMeasurements([]);
    setTrainingTypeID("");
    setPeriodisation("");
    setError(null);
    setShowResetConfirmation(false);
    // Increment resetKey to force remount of MasterSessionDetails
    setResetKey((prev) => prev + 1);
  };

  if (!isOpen) return null;

  const isFormValid = plannedMeasurements.length > 0;

  return (
    <div className="fixed inset-0 z-[9999] bg-black bg-opacity-75 backdrop-blur-sm overflow-hidden">
      <div className="flex flex-col h-full md:items-center md:justify-center md:overflow-auto">
        <div
          className="w-full max-w-2xl mx-auto bg-base-100 md:rounded-xl shadow-lg flex flex-col h-full md:h-auto md:max-h-[90vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header - Fixed on mobile */}
          <div className="flex justify-between items-center p-4 pb-6 md:p-6 md:pb-8 flex-shrink-0 bg-base-100 md:rounded-t-xl">
            <div className="flex items-center gap-2">
              <button
                onClick={onClose}
                disabled={loading}
                className="p-1 rounded-full hover:bg-base-300 bg-base-300 transition-colors disabled:opacity-50"
                style={{ touchAction: "manipulation" }}
              >
                <HiChevronLeft className="h-8 w-8 text-base-content/70" />
              </button>
              <div>
                <h2 className="text-xl text-base-content font-grotesk">
                  {existingSession ? "Edit" : "Add"} <strong>Session</strong>
                </h2>
              </div>
              {!existingSession && (
                <button
                  onClick={() => setShowResetConfirmation(true)}
                  disabled={loading}
                  className="p-2 text-base-content/50 hover:text-base-content/80 rounded-full hover:bg-base-200 transition-colors disabled:opacity-50"
                  title="Reset all fields"
                >
                  <HiArrowPath className="text-lg" />
                </button>
              )}
            </div>
            <div className="flex items-center gap-2">
              {existingSession && onDelete && (
                <button
                  onClick={() => onDelete(existingSession)}
                  disabled={loading}
                  className="p-2 text-red-600 hover:text-red-700 rounded-full hover:bg-red-500/10 transition-colors disabled:opacity-50"
                  title="Delete session"
                >
                  <HiTrash className="text-xl" />
                </button>
              )}
            </div>
          </div>

          {/* Content - Scrollable */}
          <div className="flex-1 overflow-y-auto overflow-x-visible">
            <div className="p-4 px-8 space-y-4 pb-0 bg-base-100">
              {/* Session Name (Optional) */}
              <div className="new-form-group-rounded">
                <input
                  type="text"
                  value={sessionName}
                  onChange={(e) => setSessionName(e.target.value)}
                  className="form-element-rounded"
                  placeholder=" "
                  disabled={loading}
                />
                <label>Session Title (Optional)</label>
              </div>

              {/* Session Type Selector */}
              <SearchableSelect
                options={[...trainingTypes]
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((type) => ({
                    value: type.id || "",
                    label: type.name,
                  }))}
                value={trainingTypeID}
                onChange={setTrainingTypeID}
                placeholder="Search session types..."
                allOptionLabel="None"
                emptyValue=""
                disabled={loading}
                label="Session Type (Optional)"
                inputClassName="bg-base-300"
              />

              {/* Periodisation Selector */}
              <SearchableSelect
                options={[
                  ...PERIODISATION_OPTIONS.map((option) => ({
                    value: option.value,
                    label: option.label,
                  })),
                  ...(userData?.customPeriodisationTypes || []).map((t) => ({
                    value: t,
                    label: t,
                  })),
                ].sort((a, b) => a.label.localeCompare(b.label))}
                value={periodisation}
                onChange={(value) =>
                  setPeriodisation(value as PeriodisationType | "")
                }
                placeholder="Search periodisation..."
                allOptionLabel="None"
                emptyValue=""
                disabled={loading}
                label="Periodisation (Optional)"
                inputClassName="bg-base-300"
              />

              {/* Description */}
              {/* <div className="new-form-group-rounded">
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder=" "
                rows={4}
                className="form-element-rounded resize-none"
                style={{
                  height: "100px",
                }}
                disabled={loading}
              />
              <label>Description</label>
            </div> */}
            </div>

            <div className="px-0 bg-base-100 pt-4">
              {/* Master Session Details Component */}
              <MasterSessionDetails
                key={resetKey}
                plannedMeasurements={plannedMeasurements}
                onPlannedMeasurementsChange={setPlannedMeasurements}
                initialTemplate={initialTemplate}
                loading={loading}
                error={error}
                onErrorChange={setError}
                existingSession={existingSession}
                onReset={handleReset}
                floatingActivityButton={false}
                hideFloatingAIButton
              />
            </div>
          </div>

          {/* Footer */}
          <div
            className="p-8 pb-12 flex-shrink-0 relative z-50 flex items-center gap-3"
            style={{
              pointerEvents: "auto",
              touchAction: "manipulation",
            }}
          >
            <button
              onClick={() => {
                if (document.activeElement instanceof HTMLElement) {
                  document.activeElement.blur();
                }
                setTimeout(() => {
                  handleSave();
                }, 50);
              }}
              disabled={loading || !isFormValid}
              className="bg-primary text-white py-4 px-6 font-grotesk font-bold flex-1 rounded-xl disabled:bg-primary/30 disabled:text-white/50 disabled:cursor-not-allowed"
              style={{ touchAction: "manipulation" }}
            >
              {loading
                ? existingSession
                  ? "Updating..."
                  : "Saving..."
                : existingSession
                  ? "Update Session"
                  : "Save Session"}
            </button>
          </div>
        </div>
      </div>

      {/* Reset Confirmation Dialog */}
      <Transition appear show={showResetConfirmation} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-[10000]"
          onClose={() => setShowResetConfirmation(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-base-100 p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-base-content font-grotesk"
                  >
                    Reset All Fields?
                  </Dialog.Title>
                  <div className="mt-2">
                    <p className="text-sm text-base-content/60 font-sans">
                      Are you sure you want to reset all fields? This will clear
                      all activities and selections. This action cannot be
                      undone.
                    </p>
                  </div>

                  <div className="mt-4 flex gap-3 justify-end">
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-base-300 bg-base-100 px-4 py-2 text-sm font-medium text-base-content/80 hover:bg-base-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-base-content/60 focus-visible:ring-offset-2 font-grotesk"
                      onClick={() => setShowResetConfirmation(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 font-grotesk"
                      onClick={handleReset}
                    >
                      Reset
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};

export default ManageSessionModal;
